import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { UserProfileData } from '@/types/userProfile';
import { MapPin, Calendar, Trophy, Users, Phone, Mail, Edit, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { photoUploadService } from '@/services/photoUploadService';
import { VenueUpcomingGames } from '@/components/venue/VenueUpcomingGames';
import { VenueUpcomingEvents } from '@/components/venue/VenueUpcomingEvents';
import { VenueGameSubmissionForm } from '@/components/venue/VenueGameSubmissionForm';
import { VenueEventSubmissionForm } from '@/components/venue/VenueEventSubmissionForm';
import VenueSectionsDisplay from '@/components/venue/VenueSectionsDisplay';
import { SocialActions } from '@/components/profile/SocialActions';

interface SocialMediaProfileDisplayProps {
  profile: UserProfileData;
}

export const SocialMediaProfileDisplay: React.FC<SocialMediaProfileDisplayProps> = ({ profile }) => {
  const navigate = useNavigate();
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [viewAsPublic, setViewAsPublic] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  
  const currentUser = supabaseUser || internalUser;
  const isOwnProfile = currentUser ? (
    // For Supabase users, check ID match
    (supabaseUser && supabaseUser.id === profile.id) ||
    // For internal users, check both ID and username
    (internalUser && (internalUser.id === profile.id || internalUser.username === profile.username))
  ) : false;

  // Load profile photo URL using the photo upload service
  useEffect(() => {
    const loadProfilePhoto = async () => {
      if (profile.profile_photo_url) {
        try {
          console.log('Loading profile photo for path:', profile.profile_photo_url);
          
          // For internal users, we might need to generate signed URLs
          if (profile.isInternal && !profile.profile_photo_url.startsWith('http')) {
            const signedUrl = await photoUploadService.getProfilePhotoUrl(profile.profile_photo_url);
            console.log('Profile photo signed URL:', signedUrl);
            setProfilePhotoUrl(signedUrl);
          } else {
            setProfilePhotoUrl(profile.profile_photo_url);
          }
        } catch (error) {
          console.error('Error loading profile photo:', error);
          setProfilePhotoUrl(profile.profile_photo_url);
        }
      }
    };

    loadProfilePhoto();
  }, [profile.profile_photo_url, profile.isInternal]);

  const handleEditProfile = () => {
    if (profile.isInternal) {
      navigate('/profile-edit');
    } else {
      navigate('/profile');
    }
  };

  const getVideoEmbedUrl = (url: string) => {
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const getSeasonStatusColor = (status: string) => {
    switch (status) {
      case 'in-season':
        return 'bg-green-500';
      case 'off-season':
        return 'bg-gray-500';
      case 'pre-season':
        return 'bg-yellow-500';
      case 'post-season':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getSeasonStatusText = (status: string) => {
    switch (status) {
      case 'in-season':
        return 'In Season';
      case 'off-season':
        return 'Off Season';
      case 'pre-season':
        return 'Pre Season';
      case 'post-season':
        return 'Post Season';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="relative">
        {/* Cover Photo Area with User's Profile Photo and 80% Overlay */}
        <div 
          className="h-64 rounded-t-lg relative flex items-center justify-center"
          style={{
            backgroundImage: profilePhotoUrl 
              ? `url(${profilePhotoUrl})` 
              : 'linear-gradient(to right, #16a34a, #2563eb)',
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          {/* 80% Overlay on header background */}
          {profilePhotoUrl && (
            <div className="absolute inset-0 bg-black bg-opacity-80 rounded-t-lg"></div>
          )}
        </div>
        
        {/* Profile Info */}
        <div className="relative px-6 pb-6">
          {/* Profile Picture Bubble */}
          <div className="absolute -top-16 left-6">
            <div className="w-32 h-32 rounded-full border-4 border-black overflow-hidden relative">
              {profilePhotoUrl ? (
                <img 
                  src={profilePhotoUrl} 
                  alt={`${profile.first_name} ${profile.last_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-2xl font-bold bg-gray-700">
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </div>
              )}
            </div>
          </div>

          {/* Edit Button */}
          {isOwnProfile && (
            <div className="absolute top-4 right-6">
              <Button
                onClick={handleEditProfile}
                className="bg-sports-green text-black hover:bg-green-600"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
            </div>
          )}

          {/* Profile Details */}
          <div className="pt-20">
            <div className="grid gap-6">
              {/* Name and basic info */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-white">
                    {profile.first_name} {profile.last_name}
                  </h1>
                  <p className="text-gray-400 text-lg">@{profile.username}</p>
                  
                  {/* Sport and Position */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {/* Player fields - also show for support/scorekeeper */}
                    {(profile.role === 'player' || profile.role === 'support') && profile.current_sport && (
                      <Badge variant="secondary" className="bg-sports-green/20 text-sports-green">
                        {profile.current_sport}
                      </Badge>
                    )}
                    {(profile.role === 'player' || profile.role === 'support') && profile.position && (
                      <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                        {profile.position}
                      </Badge>
                    )}
                    {(profile.role === 'player' || profile.role === 'support') && profile.jersey_number && (
                      <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                        #{profile.jersey_number}
                      </Badge>
                    )}
                    {(profile.role === 'player' || profile.role === 'support') && profile.season_status && (
                      <Badge variant="secondary" className={`${getSeasonStatusColor(profile.season_status)}/20 text-white`}>
                        <div className={`w-2 h-2 rounded-full ${getSeasonStatusColor(profile.season_status)} mr-1`}></div>
                        {getSeasonStatusText(profile.season_status)}
                      </Badge>
                    )}
                    
                    {/* Coach fields */}
                    {profile.role === 'coach' && profile.primary_sport && (
                      <Badge variant="secondary" className="bg-sports-green/20 text-sports-green">
                        {profile.primary_sport}
                      </Badge>
                    )}
                    {profile.role === 'coach' && profile.coach_type && (
                      <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                        {profile.coach_type}
                      </Badge>
                    )}
                    {profile.role === 'coach' && profile.coaching_style && (
                      <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                        {profile.coaching_style}
                      </Badge>
                    )}
                    
                    {/* Support/ScoreKeeper role styling */}
                    {profile.role === 'support' && (
                      <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
                        Score Keeper
                      </Badge>
                    )}
                    
                    {/* Support role specific details */}
                    {profile.role === 'support' && profile.support_role && (
                      <Badge variant="secondary" className="bg-purple-500/20 text-purple-400">
                        {profile.support_role}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Support Role Information Section */}
              {profile.role === 'support' && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Support Role Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profile.support_role && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Role</p>
                          <p className="text-white">{profile.support_role}</p>
                        </div>
                      )}
                      {profile.experience_level && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Experience Level</p>
                          <p className="text-white">{profile.experience_level}</p>
                        </div>
                      )}
                      {profile.years_experience && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Years of Experience</p>
                          <p className="text-white">{profile.years_experience}</p>
                        </div>
                      )}
                      {profile.certifications && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Certifications</p>
                          <p className="text-white">{profile.certifications}</p>
                        </div>
                      )}
                      {profile.previous_teams && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-medium text-gray-400 mb-1">Previous Teams</p>
                          <p className="text-white">{profile.previous_teams}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sports Preferences Section */}
              {profile.role === 'support' && profile.preferred_sports && profile.preferred_sports.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Sports Preferences</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.preferred_sports.map((sport, index) => (
                        <Badge key={index} variant="outline" className="border-gray-600 text-gray-300">
                          {sport}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Availability Section */}
              {profile.role === 'support' && profile.support_availability && profile.support_availability.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Availability</h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.support_availability.map((time, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-500/20 text-blue-400">
                          {time}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Physical Information Section */}
              {profile.role === 'support' && (profile.height || profile.weight || profile.fitness_level) && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Physical Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {profile.height && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Height</p>
                          <p className="text-white">{profile.height}</p>
                        </div>
                      )}
                      {profile.weight && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Weight</p>
                          <p className="text-white">{profile.weight}</p>
                        </div>
                      )}
                      {profile.fitness_level && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Fitness Level</p>
                          <p className="text-white">{profile.fitness_level}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Academic Details Section */}
              {profile.role === 'support' && profile.grade_level && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Academic Details</h3>
                    <div>
                      <p className="text-sm font-medium text-gray-400 mb-1">Grade Level</p>
                      <p className="text-white">{profile.grade_level}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Athletic Information Section */}
              {profile.role === 'support' && (profile.skill_level || profile.years_playing || profile.achievements) && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Athletic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profile.skill_level && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Skill Level</p>
                          <p className="text-white">{profile.skill_level}</p>
                        </div>
                      )}
                      {profile.years_playing && (
                        <div>
                          <p className="text-sm font-medium text-gray-400 mb-1">Years Playing</p>
                          <p className="text-white">{profile.years_playing}</p>
                        </div>
                      )}
                      {profile.achievements && (
                        <div className="md:col-span-2">
                          <p className="text-sm font-medium text-gray-400 mb-1">Athletic Accomplishments</p>
                          <p className="text-white">{profile.achievements}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Emergency Contact Section */}
              {profile.role === 'support' && profile.support_emergency_contact && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4">Emergency Contact</h3>
                    <p className="text-white">{profile.support_emergency_contact}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* View as Public Toggle - Only show on own profile */}
            {isOwnProfile && (
              <div className="flex items-center gap-4 bg-[#0d1117] border border-gray-700 rounded-lg px-6 py-3 mt-6">
                {viewAsPublic ? <Eye className="h-5 w-5 text-sports-green" /> : <EyeOff className="h-5 w-5 text-sports-green" />}
                <Label htmlFor="view-as-public" className="text-white font-medium cursor-pointer">
                  {viewAsPublic ? "Viewing as Public" : "View as Public"}
                </Label>
                <Switch
                  id="view-as-public"
                  checked={viewAsPublic}
                  onCheckedChange={setViewAsPublic}
                  className="data-[state=checked]:bg-sports-green data-[state=unchecked]:bg-gray-600 border-2 border-gray-500 data-[state=checked]:border-sports-green"
                />
              </div>
            )}

            {/* Social Actions - Follow button and stats */}
            <SocialActions profile={profile} viewAsPublic={viewAsPublic} />

            {/* Bio */}
            {(profile.bio || profile.player_bio) && (
              <div className="mt-6">
                <p className="text-gray-300 text-lg leading-relaxed">{profile.bio || profile.player_bio}</p>
              </div>
            )}

            {/* Contact Info */}
            {profile.show_contact_public && (
              <div className="flex flex-wrap gap-4 mt-6">
                {profile.phone && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Phone className="h-4 w-4" />
                    <span>{profile.phone}</span>
                  </div>
                )}
                {profile.email && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Mail className="h-4 w-4" />
                    <span>{profile.email}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Highlight Videos Section */}
      {profile.highlight_video_links && profile.highlight_video_links.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Trophy className="h-6 w-6" />
              Highlight Videos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.highlight_video_links.map((videoUrl, index) => (
                <div key={index} className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                  <iframe
                    src={getVideoEmbedUrl(videoUrl)}
                    className="w-full h-full"
                    allowFullScreen
                    title={`Highlight Video ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photo Gallery Section */}
      {profile.photo_gallery_links && profile.photo_gallery_links.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Users className="h-6 w-6" />
              Photo Gallery
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profile.photo_gallery_links.map((photoUrl, index) => (
                <div key={index} className="aspect-square bg-gray-900 rounded-lg overflow-hidden">
                  <img
                    src={photoUrl}
                    alt={`Gallery Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `
                          <div class="w-full h-full flex items-center justify-center bg-gray-800 text-gray-400">
                            <div class="text-center">
                              <div class="text-sm">Unable to load image</div>
                              <div class="text-xs mt-1">${photoUrl.substring(0, 30)}...</div>
                            </div>
                          </div>
                        `;
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Athletic Details */}
      {profile.role === 'player' && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Athletic Information</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {profile.skill_level && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Skill Level</div>
                  <div className="text-white font-medium capitalize">{profile.skill_level}</div>
                </div>
              )}
              {profile.years_playing && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Years Playing</div>
                  <div className="text-white font-medium">{profile.years_playing}</div>
                </div>
              )}
              {profile.height && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Height</div>
                  <div className="text-white font-medium">{profile.height}</div>
                </div>
              )}
              {profile.weight && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Weight</div>
                  <div className="text-white font-medium">{profile.weight}</div>
                </div>
              )}
              {profile.fitness_level && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Fitness Level</div>
                  <div className="text-white font-medium capitalize">{profile.fitness_level}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Player Goals */}
      {profile.role === 'player' && (profile.short_term_goals || profile.long_term_goals) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Trophy className="h-6 w-6" />
              Goals
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.short_term_goals && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Short-term Goals</div>
                  <div className="text-white whitespace-pre-wrap">{profile.short_term_goals}</div>
                </div>
              )}
              {profile.long_term_goals && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Long-term Goals</div>
                  <div className="text-white whitespace-pre-wrap">{profile.long_term_goals}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Player Academic Details */}
      {profile.role === 'player' && profile.grade_level && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Academic Details</h2>
            <div>
              <div className="text-sm text-gray-400 mb-1">Grade Level</div>
              <div className="text-white font-medium capitalize">{profile.grade_level.replace(/-/g, ' ')}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Venue Information */}
      {profile.role === 'venue' && (
        <>
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Venue Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profile.venue_name && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Venue Name</div>
                    <div className="text-white font-medium">{profile.venue_name}</div>
                  </div>
                )}
                {profile.venue_address && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Address</div>
                    <div className="text-white font-medium">{profile.venue_address}</div>
                  </div>
                )}
                {profile.venue_type && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Venue Type</div>
                    <div className="text-white font-medium">{profile.venue_type}</div>
                  </div>
                )}
                {profile.venue_capacity && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Capacity</div>
                    <div className="text-white font-medium">{profile.venue_capacity}</div>
                  </div>
                )}
                {profile.hourly_rates && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Hourly Rate</div>
                    <div className="text-white font-medium">${profile.hourly_rates}</div>
                  </div>
                )}
                {profile.available_dates && (
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Available Dates</div>
                    <div className="text-white font-medium">{profile.available_dates}</div>
                  </div>
                )}
              </div>
              {profile.venue_description && (
                <div className="mt-4">
                  <div className="text-sm text-gray-400 mb-1">Description</div>
                  <div className="text-white">{profile.venue_description}</div>
                </div>
              )}
              {profile.operating_hours && (
                <div className="mt-4">
                  <div className="text-sm text-gray-400 mb-1">Operating Hours</div>
                  <div className="text-white whitespace-pre-line">{profile.operating_hours}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Facilities Available */}
          {profile.facilities_available && profile.facilities_available.length > 0 && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Available Facilities</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.facilities_available.map((facility, index) => (
                    <Badge key={index} variant="secondary" className="bg-sports-green/20 text-sports-green">
                      {facility}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Amenities */}
          {(profile.locker_rooms_available || profile.parking_available || profile.restrooms_available || profile.concessions_available) && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Amenities</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {profile.locker_rooms_available && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-white">Locker Rooms</span>
                    </div>
                  )}
                  {profile.parking_available && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-white">Parking</span>
                    </div>
                  )}
                  {profile.restrooms_available && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-white">Restrooms</span>
                    </div>
                  )}
                  {profile.concessions_available && (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-white">Concessions</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Information */}
          {(profile.booking_contact_email || profile.venue_manager_name || profile.venue_manager_contact) && (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {profile.booking_contact_email && (
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Booking Email</div>
                      <div className="text-white font-medium">{profile.booking_contact_email}</div>
                    </div>
                  )}
                  {profile.venue_manager_name && (
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Manager Name</div>
                      <div className="text-white font-medium">{profile.venue_manager_name}</div>
                    </div>
                  )}
                  {profile.venue_manager_contact && (
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Manager Contact</div>
                      <div className="text-white font-medium">{profile.venue_manager_contact}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Venue Games and Events Sections - Only show if user has venue information */}
          {(profile.venue_name || profile.venue_address) && (
            <>
              <VenueSectionsDisplay venueId={profile.id} />
              <VenueUpcomingGames venueId={profile.id} />
              <VenueUpcomingEvents venueId={profile.id} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <VenueGameSubmissionForm venueId={profile.id} />
                <VenueEventSubmissionForm venueId={profile.id} />
              </div>
            </>
          )}
        </>
      )}

      {/* Coach Information */}
      {profile.role === 'coach' && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Coaching Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profile.years_coaching && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Years of Experience</div>
                  <div className="text-white font-medium">{profile.years_coaching} years</div>
                </div>
              )}
              {profile.primary_sport && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Primary Sport</div>
                  <div className="text-white font-medium">{profile.primary_sport}</div>
                </div>
              )}
              {profile.coach_type && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Coach Type</div>
                  <div className="text-white font-medium">{profile.coach_type}</div>
                </div>
              )}
              {profile.coaching_style && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Coaching Philosophy</div>
                  <div className="text-white font-medium">{profile.coaching_style}</div>
                </div>
              )}
              {profile.coaching_certifications && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Certifications</div>
                  <div className="text-white font-medium">{profile.coaching_certifications}</div>
                </div>
              )}
              {profile.previous_teams && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Previous Teams</div>
                  <div className="text-white font-medium">{profile.previous_teams}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coach Specializations */}
      {profile.role === 'coach' && profile.specializations && profile.specializations.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Specializations</h2>
            <div className="flex flex-wrap gap-2">
              {profile.specializations.map((spec, index) => (
                <Badge key={index} variant="secondary" className="bg-blue-500/20 text-blue-400">
                  {spec}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coach Availability */}
      {profile.role === 'coach' && profile.availability && profile.availability.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Availability</h2>
            <div className="flex flex-wrap gap-2">
              {profile.availability.map((day, index) => (
                <Badge key={index} variant="secondary" className="bg-green-500/20 text-green-400">
                  {day}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Achievements */}
      {profile.achievements && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Trophy className="h-6 w-6" />
              Achievements
            </h2>
            <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
              {profile.achievements}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sports Preferences */}
      {profile.preferred_sports && profile.preferred_sports.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Sports Interests</h2>
            <div className="flex flex-wrap gap-2">
              {profile.preferred_sports.map((sport, index) => (
                <Badge key={index} variant="secondary" className="bg-sports-green/20 text-sports-green">
                  {sport}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fan Information */}
      {profile.role === 'fan' && (profile.favorite_teams?.length || profile.favorite_sports?.length || profile.fan_since) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Fan Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.fan_since && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Fan Since</div>
                  <div className="text-white font-medium">{profile.fan_since}</div>
                </div>
              )}
              {profile.favorite_teams && profile.favorite_teams.length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-2">Favorite Teams</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.favorite_teams.map((team, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-500/20 text-blue-400">
                        {team}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {profile.favorite_sports && profile.favorite_sports.length > 0 && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-2">Favorite Sports</div>
                  <div className="flex flex-wrap gap-2">
                    {profile.favorite_sports.map((sport, index) => (
                      <Badge key={index} variant="secondary" className="bg-sports-green/20 text-sports-green">
                        {sport}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parent Information */}
      {profile.role === 'parent' && (profile.children_info || profile.involvement_level || profile.parenting_sports_involvement) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Users className="h-6 w-6" />
              Parent Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.involvement_level && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Involvement Level</div>
                  <div className="text-white font-medium capitalize">{profile.involvement_level}</div>
                </div>
              )}
              {profile.children_info && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Children Information</div>
                  <div className="text-white whitespace-pre-wrap">{profile.children_info}</div>
                </div>
              )}
              {profile.parenting_sports_involvement && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Sports Involvement</div>
                  <div className="text-white whitespace-pre-wrap">{profile.parenting_sports_involvement}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parent Availability */}
      {profile.role === 'parent' && profile.availability && profile.availability.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Availability</h2>
            <div className="flex flex-wrap gap-2">
              {profile.availability.map((day, index) => (
                <Badge key={index} variant="secondary" className="bg-green-500/20 text-green-400">
                  {day}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Owner Information */}
      {profile.role === 'team_owner' && (profile.sponsorships || profile.venue_selection || profile.team_goals || profile.team_preferences) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Owner Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.sponsorships && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Sponsorships</div>
                  <div className="text-white whitespace-pre-wrap">{profile.sponsorships}</div>
                </div>
              )}
              {profile.venue_selection && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Venue Selection Criteria</div>
                  <div className="text-white whitespace-pre-wrap">{profile.venue_selection}</div>
                </div>
              )}
              {profile.team_goals && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Team Goals</div>
                  <div className="text-white whitespace-pre-wrap">{profile.team_goals}</div>
                </div>
              )}
              {profile.team_preferences && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Team Preferences</div>
                  <div className="text-white whitespace-pre-wrap">{profile.team_preferences}</div>
                </div>
              )}
              {profile.facilities_needs && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Facility Needs</div>
                  <div className="text-white whitespace-pre-wrap">{profile.facilities_needs}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Owner Facilities */}
      {profile.role === 'team_owner' && profile.facilities_owned && profile.facilities_owned.length > 0 && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Owned Facilities</h2>
            <div className="flex flex-wrap gap-2">
              {profile.facilities_owned.map((facility, index) => (
                <Badge key={index} variant="secondary" className="bg-sports-green/20 text-sports-green">
                  {facility}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Creative Professional Information */}
      {profile.role === 'creative' && (profile.creative_role || profile.creative_specializations || profile.portfolio_link || profile.years_creative_experience) && (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-white mb-6">Creative Professional</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {profile.creative_role && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Role</div>
                  <div className="text-white font-medium">{profile.creative_role}</div>
                </div>
              )}
              {profile.years_creative_experience && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Years of Experience</div>
                  <div className="text-white font-medium">{profile.years_creative_experience}</div>
                </div>
              )}
              {profile.creative_specializations && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Specializations</div>
                  <div className="text-white">{profile.creative_specializations}</div>
                </div>
              )}
              {profile.equipment_used && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Equipment</div>
                  <div className="text-white">{profile.equipment_used}</div>
                </div>
              )}
              {profile.creative_certifications && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Certifications</div>
                  <div className="text-white">{profile.creative_certifications}</div>
                </div>
              )}
              {profile.creative_availability && (
                <div>
                  <div className="text-sm text-gray-400 mb-1">Availability</div>
                  <div className="text-white">{profile.creative_availability}</div>
                </div>
              )}
              {profile.portfolio_link && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Portfolio</div>
                  <a href={profile.portfolio_link} target="_blank" rel="noopener noreferrer" className="text-sports-green hover:underline">
                    {profile.portfolio_link}
                  </a>
                </div>
              )}
              {profile.notable_projects && (
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-400 mb-1">Notable Projects</div>
                  <div className="text-white whitespace-pre-wrap">{profile.notable_projects}</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
